import { State, Action, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { SalesSummary } from '@sales/models/sales-summary';
import {
  ApiSalesSummaryAction,
  GetEmergencyAlertResponse,
  ResetSaleStatusAction,
  SetEmergencyAlertResponse,
  SetHasActiveSaleAction,
} from '@sales/states/sale/sale.actions';
import { SalesSummaryService } from '@sales/services/sales-summary.service';
import { catchError, tap } from 'rxjs/operators';
import { EmergencyAlertResponse } from '@core/models/emergency-alert-response.model';
import { EmergencyAlertService } from '@app/core/services/emergency-alert.service';

export interface SaleStateModel {
  hasActiveSale: boolean;
  summary: SalesSummary;
  emergencyAlertResponse: EmergencyAlertResponse;
}

const initialEmergencyAlertResponse: EmergencyAlertResponse = {
  alertList: [],
  callbackSentToTimer: false,
};

const initialSaleState: SaleStateModel = {
  hasActiveSale: false,
  summary: {
    salesToday: 0,
    salesThisWeek: 0,
    salesThisMonth: 0,
  },
  emergencyAlertResponse: initialEmergencyAlertResponse,
};

@State<SaleStateModel>({
  name: 'sale',
  defaults: initialSaleState,
})
@Injectable()
export class SaleState {
  private salesSummaryService = inject(SalesSummaryService);
  private emergencyAlertService = inject(EmergencyAlertService);

  @Action(SetHasActiveSaleAction)
  public setHasActiveSale({ patchState }: StateContext<SaleStateModel>, { hasActiveSale }) {
    patchState({ hasActiveSale });
  }

  @Action(ResetSaleStatusAction)
  public resetSaleStatus({ patchState }: StateContext<SaleStateModel>) {
    patchState({ hasActiveSale: false });
  }

  @Action(SetEmergencyAlertResponse)
  public setEmergencyAlertResponse(
    { patchState }: StateContext<SaleStateModel>,
    { emergencyAlertResponse }: SetEmergencyAlertResponse
  ) {
    patchState({ emergencyAlertResponse });
  }

  @Action(ApiSalesSummaryAction)
  public apiSalesSummary({ patchState }: StateContext<SaleStateModel>) {
    return this.salesSummaryService.salesSummary().pipe(
      tap(response => patchState({ summary: response })),
      catchError(error => {
        patchState({ summary: null });
        throw error;
      })
    );
  }

  @Action(GetEmergencyAlertResponse)
  public getEmergencyAlertResponse({ patchState }: StateContext<SaleStateModel>) {
    return this.emergencyAlertService.messages().pipe(
      tap(response => patchState({ emergencyAlertResponse: response })),
      catchError(error => {
        patchState({ emergencyAlertResponse: null });
        throw error;
      })
    );
  }
}
